import { DeviceTelemetryType } from '@signageos/common-types/dist/Device/Telemetry/DeviceTelemetryType';
import { MonitoringLogData } from '@signageos/common-types/dist/Device/MonitoringLog/MonitoringLogData';
import { IPropertyStorage } from '../../../Property/propertyStorage';
import Property from '../../../Property/Property';
import Debug from 'debug';

const debug = Debug('@signageos/front-display:Management:Telemetry:telemetryStackStorage');
const MAX_STACK_SIZE = 10;

export interface TelemetryStackEntry<T extends DeviceTelemetryType> {
	type: T;
	data: MonitoringLogData[T];
}

export interface TelemetryStackConfig {
	maxStackSize?: number;
	keyPrefix?: string;
}

export class TelemetryStackStorage {
	private readonly maxStackSize: number;
	private readonly keyPrefix: string;
	private readonly propertyStorage: IPropertyStorage;

	constructor(propertyStorage: IPropertyStorage, config: TelemetryStackConfig = {}) {
		this.propertyStorage = propertyStorage;
		this.maxStackSize = config.maxStackSize ?? MAX_STACK_SIZE;
		this.keyPrefix = config.keyPrefix ?? 'telemetry_stack_';
	}

	private getStorageKey<T extends DeviceTelemetryType>(type: T): string {
		return `${this.keyPrefix}${type}`;
	}

	async pushTelemetry<T extends DeviceTelemetryType>(type: T, data: MonitoringLogData[T]): Promise<void> {
		// Only store CPU and Memory usage telemetry types
		const allowedTelemetryTypes = [DeviceTelemetryType.CPU_USAGE, DeviceTelemetryType.MEMORY_USAGE];
		if (!allowedTelemetryTypes.includes(type)) {
			return;
		}

		try {
			const key = this.getStorageKey(type);
			const stack = await this.getStack(type);

			const entry: TelemetryStackEntry<T> = {
				type,
				data,
			};

			stack.unshift(entry);

			if (stack.length > this.maxStackSize) {
				stack.splice(this.maxStackSize);
			}

			// Store the updated stack in the combined telemetry storage
			const allTelemetryData = await this.propertyStorage.getValueOrDefault(Property.TELEMETRY_STACK_STORAGE, {});
			(allTelemetryData as any)[key] = stack;
			await this.propertyStorage.setValue(Property.TELEMETRY_STACK_STORAGE, allTelemetryData);

			debug(`Pushed telemetry ${type} to stack, stack size: ${stack.length}, data:`, data);
		} catch (error) {
			debug(`Failed to push telemetry ${type} to stack:`, error);
		}
	}

	async getStack<T extends DeviceTelemetryType>(type: T): Promise<TelemetryStackEntry<T>[]> {
		try {
			const key = this.getStorageKey(type);
			const allTelemetryData = await this.propertyStorage.getValueOrDefault(Property.TELEMETRY_STACK_STORAGE, {});
			const stackData = (allTelemetryData as any)[key];

			if (!stackData) {
				return [];
			}

			if (!Array.isArray(stackData)) {
				debug(`Invalid stack format for ${type}, resetting`);
				return [];
			}

			return stackData as TelemetryStackEntry<T>[];
		} catch (error) {
			debug(`Failed to get stack for ${type}:`, error);
			return [];
		}
	}

	async getLastEntries<T extends DeviceTelemetryType>(type: T, count: number): Promise<TelemetryStackEntry<T>[]> {
		const stack = await this.getStack(type);
		return stack.slice(0, count);
	}
}

// Note: The telemetryStackStorage instance needs to be created with PropertyStorage dependency
// This will be done through the dependency injection system where PropertyStorage is available
export let telemetryStackStorage: TelemetryStackStorage;

/**
 * Initialize the telemetry stack storage with PropertyStorage
 * This function should be called during application initialization
 */
export function initializeTelemetryStackStorage(propertyStorage: IPropertyStorage, config?: TelemetryStackConfig) {
	telemetryStackStorage = new TelemetryStackStorage(propertyStorage, config);
}
